import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import {
  createChart,
  createSeriesMarkers,
  ColorType,
  CrosshairMode,
  LineSeries,
  AreaSeries,
  type Time,
} from 'lightweight-charts';

export interface RegimeDiagnostics {
  markov_score?: number;
  regime_label?: string;
  bull_probability?: number;
  bear_probability?: number;
  neutral_probability?: number;
  transition_risk?: number;
  confidence?: number;
  persistence?: number;
  weight_multiplier?: number;
  // Trend overlay (Task #81)
  trend_regime_label?: string;
  final_regime_label?: string;
  drawdown_from_63d_high?: number;
  return_10d?: number;
  return_20d?: number;
  close_vs_sma50_pct?: number;
  sma20_vs_sma50_pct?: number;
}

export interface AllocationRecord {
  date: string;
  close_price: number | null;
  target_weight: number;
  prev_weight: number;
  weight_change: number;
  event_type: string | null;
  reason?: Record<string, unknown> | null;
  indicators: Record<string, number>;
  all_indicators?: Record<string, number>;
  regime?: RegimeDiagnostics;
}

interface AllocationChartProps {
  data: AllocationRecord[];
  symbol: string;
  indicatorNames?: string[];
}

const EVENT_COLORS: Record<string, string> = {
  entry: '#10b981',
  increase: '#3b82f6',
  decrease: '#f59e0b',
  exit: '#ef4444',
};

const INDICATOR_PALETTE = [
  '#7c3aed', '#0891b2', '#b45309', '#be185d',
  '#0f766e', '#c2410c', '#1d4ed8', '#15803d',
];

// Prefixes whose values share the price scale → drawn on the price chart
const OVERLAY_PREFIXES = [
  'moving_average_price', 'sma', 'ema',
  'exponential_moving_average', 'current_price',
];

function isOverlayIndicator(name: string): boolean {
  const lower = name.toLowerCase();
  return OVERLAY_PREFIXES.some((p) => lower.startsWith(p));
}

// ---- Client-side downsampling (Largest-Triangle-Three-Buckets) ----
// Preserves visual fidelity and always retains event days.

const MAX_CHART_POINTS = 500;

function lttb<T extends { value: number }>(data: T[], threshold: number): T[] {
  if (data.length <= threshold) return data;
  const sampled: T[] = [data[0]];
  const bucketSize = (data.length - 2) / (threshold - 2);
  let a = 0;
  for (let i = 0; i < threshold - 2; i++) {
    const rangeStart = Math.floor((i + 1) * bucketSize) + 1;
    const rangeEnd = Math.min(Math.floor((i + 2) * bucketSize) + 1, data.length);
    let avgX = 0, avgY = 0;
    for (let j = rangeStart; j < rangeEnd; j++) {
      avgX += j;
      avgY += data[j].value;
    }
    const rangeLen = rangeEnd - rangeStart;
    avgX /= rangeLen;
    avgY /= rangeLen;

    const rangeOfA = Math.floor(i * bucketSize) + 1;
    const rangeOfAEnd = Math.min(Math.floor((i + 1) * bucketSize) + 1, data.length);

    let maxArea = -1;
    let nextA = rangeOfA;
    for (let j = rangeOfA; j < rangeOfAEnd; j++) {
      const area = Math.abs((a - avgX) * (data[j].value - data[a].value) - (a - j) * (avgY - data[a].value)) * 0.5;
      if (area > maxArea) { maxArea = area; nextA = j; }
    }
    sampled.push(data[nextA]);
    a = nextA;
  }
  sampled.push(data[data.length - 1]);
  return sampled;
}

function downsampleWithEvents(data: AllocationRecord[], maxPoints: number): AllocationRecord[] {
  if (data.length <= maxPoints) return data;
  const eventDates = new Set(data.filter((r) => r.event_type).map((r) => r.date));
  const nonEvents = data.filter((r) => !r.event_type);
  const budget = Math.max(maxPoints - eventDates.size, Math.floor(maxPoints * 0.5));
  const sampledNonEvents = lttb(
    nonEvents.map((r) => ({ ...r, value: r.close_price ?? r.target_weight })),
    budget
  ).map(({ value: _v, ...rest }) => rest as AllocationRecord);
  const combined = [...sampledNonEvents, ...data.filter((r) => r.event_type)];
  return combined.sort((a, b) => a.date.localeCompare(b.date));
}

const INDICATOR_LS_COLLAPSED = 'allocationChart_indicatorCollapsed';
const INDICATOR_LS_HEIGHT = 'allocationChart_indicatorHeight_v2';
const INDICATOR_MIN_HEIGHT = 140;
const INDICATOR_DEFAULT_HEIGHT = 400;
const INDICATOR_HEADER_HEIGHT = 24;

export function AllocationChart({ data, symbol, indicatorNames = [] }: AllocationChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const priceContainerRef = useRef<HTMLDivElement>(null);
  const weightContainerRef = useRef<HTMLDivElement>(null);
  const indicatorContainerRef = useRef<HTMLDivElement>(null);
  const chartsRef = useRef<ReturnType<typeof createChart>[]>([]);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const [indicatorCollapsed, setIndicatorCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(INDICATOR_LS_COLLAPSED) === 'true'; } catch { return false; }
  });
  const [indicatorHeight, setIndicatorHeight] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(INDICATOR_LS_HEIGHT);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed)) return Math.max(INDICATOR_MIN_HEIGHT, parsed);
      }
    } catch { /* ignore */ }
    return INDICATOR_DEFAULT_HEIGHT;
  });
  const dragStateRef = useRef<{ startY: number; startH: number } | null>(null);
  const indicatorChartRef = useRef<ReturnType<typeof createChart> | null>(null);
  // Mirror state into a ref so the main chart useEffect can read latest height
  // without listing indicatorHeight as a dependency (avoiding full chart rebuilds on drag).
  const indicatorHeightRef = useRef(indicatorHeight);
  useEffect(() => { indicatorHeightRef.current = indicatorHeight; }, [indicatorHeight]);

  const toggleCollapsed = useCallback(() => {
    setIndicatorCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(INDICATOR_LS_COLLAPSED, String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragStateRef.current = { startY: e.clientY, startH: indicatorHeightRef.current };
    const onMove = (ev: MouseEvent) => {
      if (!dragStateRef.current) return;
      const delta = ev.clientY - dragStateRef.current.startY;
      const newH = Math.max(INDICATOR_MIN_HEIGHT, dragStateRef.current.startH + delta);
      // Update the chart directly — no full chart rebuild needed
      if (indicatorChartRef.current) {
        indicatorChartRef.current.applyOptions({ height: newH });
      }
      setIndicatorHeight(newH);
      indicatorHeightRef.current = newH;
      try { localStorage.setItem(INDICATOR_LS_HEIGHT, String(newH)); } catch { /* ignore */ }
    };
    const onUp = () => {
      dragStateRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const destroyCharts = useCallback(() => {
    chartsRef.current.forEach((c) => {
      try { c.remove(); } catch { /* ignore */ }
    });
    chartsRef.current = [];
  }, []);

  // ---- Indicator filter state ----
  const [activeIndicators, setActiveIndicators] = useState<Set<string>>(
    () => new Set(indicatorNames)
  );

  // When indicatorNames changes (new symbol/strategy), reset to show all.
  useEffect(() => {
    setActiveIndicators(new Set(indicatorNames));
  }, [indicatorNames]);

  // Split into overlay (price chart) vs panel (separate chart below)
  const overlayIndicatorNames = useMemo(
    () => indicatorNames.filter(isOverlayIndicator),
    [indicatorNames]
  );
  const panelIndicatorNames = useMemo(
    () => indicatorNames.filter((n) => !isOverlayIndicator(n)),
    [indicatorNames]
  );
  const visibleOverlayNames = useMemo(
    () => overlayIndicatorNames.filter((n) => activeIndicators.has(n)),
    [overlayIndicatorNames, activeIndicators]
  );
  const visiblePanelNames = useMemo(
    () => panelIndicatorNames.filter((n) => activeIndicators.has(n)),
    [panelIndicatorNames, activeIndicators]
  );

  // Stable color map — colors are assigned by position in the full list so
  // toggling an indicator doesn't shift the colors of the others.
  const indicatorColorMap = useMemo(
    () => new Map(indicatorNames.map((n, i) => [n, INDICATOR_PALETTE[i % INDICATOR_PALETTE.length]])),
    [indicatorNames]
  );

  const toggleIndicator = useCallback((name: string) => {
    setActiveIndicators((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }, []);

  const displayData = useMemo(() => downsampleWithEvents(data, MAX_CHART_POINTS), [data]);

  // Panel is shown only for non-overlay indicators.
  const hasIndicators = panelIndicatorNames.length > 0 &&
    data.some((r) => Object.keys(r.indicators).length > 0);
  const hasAnyIndicators = indicatorNames.length > 0 &&
    data.some((r) => Object.keys(r.indicators).length > 0);

  useEffect(() => {
    if (!priceContainerRef.current || !weightContainerRef.current || displayData.length === 0) {
      return;
    }
    destroyCharts();

    const sharedOpts = {
      layout: {
        background: { type: ColorType.Solid, color: '#ffffff' },
        textColor: '#374151',
        fontSize: 11,
      },
      grid: {
        vertLines: { color: '#f3f4f6' },
        horzLines: { color: '#f3f4f6' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: '#e5e7eb' },
      timeScale: { borderColor: '#e5e7eb', timeVisible: false, secondsVisible: false },
      handleScroll: true,
      handleScale: true,
    };

    // ---- Price chart ----
    const priceChart = createChart(priceContainerRef.current, { ...sharedOpts, height: 440 });

    const priceSeries = priceChart.addSeries(LineSeries, {
      color: '#059669',
      lineWidth: 2,
      title: symbol,
    });

    const priceData = displayData
      .filter((r) => r.close_price != null)
      .map((r) => ({ time: r.date as Time, value: r.close_price as number }));
    if (priceData.length > 0) priceSeries.setData(priceData);

    const eventMarkers = displayData
      .filter((r) => r.event_type !== null)
      .map((r) => ({
        time: r.date as Time,
        position: 'belowBar' as const,
        color: EVENT_COLORS[r.event_type!] ?? '#6b7280',
        shape: 'arrowUp' as const,
        text: r.event_type!.charAt(0).toUpperCase(),
        size: 1,
      }));
    if (eventMarkers.length > 0) {
      createSeriesMarkers(priceSeries, eventMarkers);
    }

    // ---- Overlay indicators on price chart ----
    visibleOverlayNames.forEach((name) => {
      const color = indicatorColorMap.get(name) ?? '#94a3b8';
      const seriesData = displayData
        .filter((r) => r.indicators[name] != null)
        .map((r) => ({ time: r.date as Time, value: r.indicators[name] }));
      if (seriesData.length === 0) return;
      const s = priceChart.addSeries(LineSeries, {
        color,
        lineWidth: 1,
        lineStyle: 2, // dashed
        title: name.length > 28 ? name.slice(0, 26) + '…' : name,
      });
      s.setData(seriesData);
    });

    // ---- Weight chart ----
    const weightChart = createChart(weightContainerRef.current, { ...sharedOpts, height: 140 });

    const weightSeries = weightChart.addSeries(AreaSeries, {
      lineColor: '#6366f1',
      topColor: 'rgba(99,102,241,0.3)',
      bottomColor: 'rgba(99,102,241,0.02)',
      lineWidth: 2,
      title: 'Weight %',
    });

    const weightData = displayData.map((r) => ({
      time: r.date as Time,
      value: +(r.target_weight * 100).toFixed(4),
    }));
    weightSeries.setData(weightData);

    // ---- Indicator chart (panel — non-overlay only) ----
    let indicatorChart: ReturnType<typeof createChart> | null = null;

    indicatorChartRef.current = null;
    if (visiblePanelNames.length > 0 && !indicatorCollapsed && indicatorContainerRef.current) {
      indicatorChart = createChart(indicatorContainerRef.current, { ...sharedOpts, height: indicatorHeightRef.current });
      indicatorChartRef.current = indicatorChart;

      // All panel indicators share a single right price scale so the y-axis is visible
      indicatorChart!.priceScale('right').applyOptions({
        scaleMargins: { top: 0.1, bottom: 0.1 },
        visible: true,
        minimumWidth: 55,
      });

      visiblePanelNames.forEach((name, idx) => {
        const color = indicatorColorMap.get(name) ?? INDICATOR_PALETTE[idx % INDICATOR_PALETTE.length];
        const seriesData = displayData
          .filter((r) => r.indicators[name] != null)
          .map((r) => ({ time: r.date as Time, value: r.indicators[name] }));
        if (seriesData.length === 0) return;
        const series = indicatorChart!.addSeries(LineSeries, {
          color,
          lineWidth: 1,
          title: name.length > 30 ? name.slice(0, 28) + '…' : name,
          priceScaleId: 'right',
        });
        series.setData(seriesData);
      });
    }

    // ---- Sync time scales across all charts ----
    const allCharts = [priceChart, weightChart, ...(indicatorChart ? [indicatorChart] : [])];
    let syncing = false;

    allCharts.forEach((source) => {
      source.timeScale().subscribeVisibleLogicalRangeChange((range) => {
        if (syncing || !range) return;
        syncing = true;
        allCharts.forEach((target) => {
          if (target !== source) target.timeScale().setVisibleLogicalRange(range);
        });
        syncing = false;
      });
    });

    // ---- Crosshair tooltip (looks up full-resolution record by date) ----
    const tooltip = tooltipRef.current;
    const container = containerRef.current;

    type CrosshairParam = Parameters<Parameters<typeof priceChart.subscribeCrosshairMove>[0]>[0];
    const showTooltip = (param: CrosshairParam) => {
      if (!tooltip || !container) return;
      if (!param.time || !param.point) { tooltip.style.display = 'none'; return; }
      const dateStr = param.time as string;
      // Look up from full-resolution data for tooltip accuracy
      const record = data.find((r) => r.date === dateStr);
      if (!record) { tooltip.style.display = 'none'; return; }

      const pct = (record.target_weight * 100).toFixed(2);
      const changeNum = record.weight_change * 100;
      const changePctStr = (changeNum >= 0 ? '+' : '') + changeNum.toFixed(2);
      const eventColor = record.event_type ? EVENT_COLORS[record.event_type] : '';

      const eventHtml = record.event_type
        ? `<div style="margin-top:3px">Event: <span style="color:${eventColor};font-weight:700">${record.event_type.toUpperCase()}</span></div>`
        : '';

      const formatValue = (v: unknown): string => {
        if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(4);
        if (typeof v === 'boolean') return v ? 'Yes' : 'No';
        if (v === null || v === undefined) return '—';
        return String(v).slice(0, 40);
      };

      const TOOLTIP_MAX_ROWS = 8;

      const renderEntries = (entries: [string, unknown][]): string => {
        const visible = entries.slice(0, TOOLTIP_MAX_ROWS);
        const overflow = entries.length - visible.length;
        const rows = visible.map(([k, v]) => {
          const shortKey = k.length > 32 ? k.slice(0, 30) + '…' : k;
          return `<div style="font-size:11px;color:#6b7280">${shortKey}: <b>${formatValue(v)}</b></div>`;
        });
        if (overflow > 0) {
          rows.push(`<div style="font-size:10px;color:#9ca3af;margin-top:1px">+${overflow} more…</div>`);
        }
        return rows.join('');
      };

      const indicatorEntries = Object.entries(record.indicators ?? {});
      const indicatorHtml = renderEntries(indicatorEntries);

      // Show event reason only when it carries data not already shown in indicators
      const reasonOnlyEntries = Object.entries(record.reason ?? {}).filter(
        ([k]) => !(k in (record.indicators ?? {}))
      );
      const reasonHtml = renderEntries(reasonOnlyEntries);

      tooltip.innerHTML = `
        <div style="font-weight:700;margin-bottom:5px;font-size:12px">${dateStr}</div>
        ${record.close_price != null ? `<div>Price: <b>$${record.close_price.toFixed(2)}</b></div>` : ''}
        <div>Weight: <b>${pct}%</b></div>
        <div>Change: <b>${changePctStr}%</b></div>
        ${eventHtml}
        ${indicatorHtml ? `
          <div style="margin-top:4px;border-top:1px solid #e5e7eb;padding-top:4px">
            <div style="font-size:10px;font-weight:600;color:#9ca3af;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:3px">Indicators</div>
            ${indicatorHtml}
          </div>` : ''}
        ${reasonHtml ? `
          <div style="margin-top:4px;border-top:1px solid #e5e7eb;padding-top:4px">
            <div style="font-size:10px;font-weight:600;color:#9ca3af;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:3px">Decision Reason</div>
            ${reasonHtml}
          </div>` : ''}
      `;

      tooltip.style.display = 'block';
    };

    allCharts.forEach((chart) => chart.subscribeCrosshairMove(showTooltip));
    allCharts.forEach((chart) => chart.timeScale().fitContent());
    chartsRef.current = allCharts;

    // ---- Resize observer ----
    const ro = new ResizeObserver(() => {
      if (priceContainerRef.current) priceChart.applyOptions({ width: priceContainerRef.current.offsetWidth });
      if (weightContainerRef.current) weightChart.applyOptions({ width: weightContainerRef.current.offsetWidth });
      if (indicatorChart && indicatorContainerRef.current) {
        indicatorChart.applyOptions({ width: indicatorContainerRef.current.offsetWidth });
      }
    });
    if (priceContainerRef.current) ro.observe(priceContainerRef.current);
    if (weightContainerRef.current) ro.observe(weightContainerRef.current);
    if (indicatorContainerRef.current) ro.observe(indicatorContainerRef.current);

    return () => { ro.disconnect(); destroyCharts(); };
  }, [displayData, data, symbol, hasIndicators, visibleOverlayNames, visiblePanelNames, indicatorColorMap, destroyCharts, indicatorCollapsed]);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
        No data available for this symbol and date range.
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Info panel — fixed to the right edge of the viewport, never overlaps chart */}
      <div
        ref={tooltipRef}
        style={{
          display: 'none', position: 'fixed', right: 16, top: 80, zIndex: 100,
          background: 'rgba(255,255,255,0.97)', border: '1px solid #e5e7eb',
          borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#374151',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', pointerEvents: 'none',
          width: 220, maxHeight: '70vh', overflowY: 'auto', lineHeight: 1.6,
        }}
      />

      {/* Weight panel */}
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 px-1">
        Target Weight (%)
      </div>
      <div ref={weightContainerRef} className="w-full" />

      {/* Price panel */}
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 mt-4 px-1">
        {symbol} — Close Price
        {data.length > MAX_CHART_POINTS && (
          <span className="ml-2 font-normal text-gray-400 normal-case">
            (showing {displayData.length} of {data.length} days, events preserved)
          </span>
        )}
      </div>
      <div ref={priceContainerRef} className="w-full" />

      {/* Event legend */}
      <div className="flex flex-wrap gap-4 mt-3 mb-2 px-1 text-xs text-gray-600">
        {Object.entries(EVENT_COLORS).map(([type, color]) => (
          <span key={type} className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }} />
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </span>
        ))}
      </div>

      {/* Indicator controls — shown when any indicators exist */}
      {hasAnyIndicators && (
        <div className="mt-3 mb-1 px-1">
          <div className="flex items-center gap-2 flex-wrap">
            {/* All / None quick buttons */}
            <button
              onClick={() => setActiveIndicators(new Set(indicatorNames))}
              className="px-2 py-0.5 text-xs rounded border border-gray-300 text-gray-500 hover:bg-gray-100 transition-colors shrink-0"
            >All</button>
            <button
              onClick={() => setActiveIndicators(new Set())}
              className="px-2 py-0.5 text-xs rounded border border-gray-300 text-gray-500 hover:bg-gray-100 transition-colors shrink-0"
            >None</button>

            {/* Overlay group */}
            {overlayIndicatorNames.length > 0 && (
              <>
                <span className="text-gray-300 text-xs select-none shrink-0">|</span>
                <span className="text-xs text-gray-400 font-medium shrink-0">Price chart:</span>
                {overlayIndicatorNames.map((name) => {
                  const color = indicatorColorMap.get(name) ?? '#6b7280';
                  const isActive = activeIndicators.has(name);
                  return (
                    <button
                      key={name}
                      onClick={() => toggleIndicator(name)}
                      title={name}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs transition-all"
                      style={{
                        borderColor: isActive ? color : '#d1d5db',
                        background: isActive ? `${color}18` : 'transparent',
                        color: isActive ? color : '#9ca3af',
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      {/* Dashed line icon for overlays */}
                      <span className="inline-flex items-center gap-0.5 shrink-0">
                        <span className="inline-block w-1.5 h-px" style={{ background: isActive ? color : '#d1d5db' }} />
                        <span className="inline-block w-1.5 h-px" style={{ background: isActive ? color : '#d1d5db' }} />
                      </span>
                      <span className="max-w-[140px] truncate">{name}</span>
                    </button>
                  );
                })}
              </>
            )}

            {/* Panel group */}
            {panelIndicatorNames.length > 0 && (
              <>
                <span className="text-gray-300 text-xs select-none shrink-0">|</span>
                <span className="text-xs text-gray-400 font-medium shrink-0">Indicator pane:</span>
                {panelIndicatorNames.map((name) => {
                  const color = indicatorColorMap.get(name) ?? '#6b7280';
                  const isActive = activeIndicators.has(name);
                  return (
                    <button
                      key={name}
                      onClick={() => toggleIndicator(name)}
                      title={name}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs transition-all"
                      style={{
                        borderColor: isActive ? color : '#d1d5db',
                        background: isActive ? `${color}18` : 'transparent',
                        color: isActive ? color : '#9ca3af',
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      <span
                        className="inline-block w-2 h-2 rounded-full shrink-0"
                        style={{ background: isActive ? color : '#d1d5db' }}
                      />
                      <span className="max-w-[140px] truncate">{name}</span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}

      {/* Indicator panel — non-overlay indicators in resizable pane */}
      {hasIndicators && (
        <div className="mt-2">
          <div className="flex items-center gap-2 mb-1 px-1" style={{ minHeight: INDICATOR_HEADER_HEIGHT }}>
            <button
              onClick={toggleCollapsed}
              className="flex items-center justify-center w-5 h-5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              title={indicatorCollapsed ? 'Expand indicator panel' : 'Collapse indicator panel'}
              aria-label={indicatorCollapsed ? 'Expand indicator panel' : 'Collapse indicator panel'}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className="w-3.5 h-3.5"
                style={{ transform: indicatorCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
              >
                <path fillRule="evenodd" d="M8 10.5a.75.75 0 0 1-.53-.22l-4-4a.75.75 0 1 1 1.06-1.06L8 8.69l3.47-3.47a.75.75 0 1 1 1.06 1.06l-4 4a.75.75 0 0 1-.53.22Z" clipRule="evenodd" />
              </svg>
            </button>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Strategy Indicators
            </span>
          </div>

          {!indicatorCollapsed && (
            <div className="relative">
              <div ref={indicatorContainerRef} className="w-full" />
              <div
                onMouseDown={onDragStart}
                className="absolute bottom-0 left-0 right-0 h-2 flex items-center justify-center cursor-ns-resize group"
                title="Drag to resize"
              >
                <div className="w-10 h-1 rounded-full bg-gray-200 group-hover:bg-indigo-400 transition-colors" />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
